import jsPDF from 'jspdf';

import Papa from 'papaparse';
import { trackFeatureUsage } from './analyticsService';

/**
 * Export Service
 * Handles PDF and CSV export functionality for dashboards and drill-down analysis
 */

/**
 * Generate PDF from dashboard data
 * @param {Object} options - Export options
 * @param {string} options.title - Report title
 * @param {Object} options.data - Data to export
 * @param {string} options.type - Export type (executive-dashboard, drill-down)
 */
export async function exportToPDF(options) {
  const { title, data, type } = options;
  
  try {
    trackFeatureUsage('pdf_export', { type, title });
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Add header with logo and title
    pdf?.setFontSize(20);
    pdf?.setTextColor(30, 58, 138); // Primary blue color
    pdf?.text(title, margin, yPosition);
    yPosition += 10;

    pdf?.setFontSize(10);
    pdf?.setTextColor(100, 100, 100);
    pdf?.text(`Generated on: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
    yPosition += 15;

    // Add content based on type
    if (type === 'executive-dashboard') {
      yPosition = await addExecutiveDashboardContent(pdf, data, yPosition, margin, pageWidth, pageHeight);
    } else if (type === 'drill-down') {
      yPosition = await addDrillDownContent(pdf, data, yPosition, margin, pageWidth, pageHeight);
    }

    // Add footer
    const totalPages = pdf?.internal?.pages?.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf?.setPage(i);
      pdf?.setFontSize(8);
      pdf?.setTextColor(150, 150, 150);
      pdf?.text(
        `Page ${i} of ${totalPages} | Decision Intelligence System`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const fileName = `${title?.replace(/\s+/g, '_')}_${new Date()?.getTime()}.pdf`;
    pdf?.save(fileName);
    
    return { success: true, fileName };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Add executive dashboard content to PDF
 */
async function addExecutiveDashboardContent(pdf, data, yPosition, margin, pageWidth, pageHeight) {
  const maxY = pageHeight - 20;

  // Business Anchors Section
  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text('Business Anchors', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(0, 0, 0);
  
  const anchors = [
    { label: 'Total Revenue', value: data?.businessAnchors?.totalRevenue?.value, trend: data?.businessAnchors?.totalRevenue?.trend },
    { label: 'Total Cost', value: data?.businessAnchors?.totalCost?.value, trend: data?.businessAnchors?.totalCost?.trend },
    { label: 'Gross Profit', value: data?.businessAnchors?.grossProfit?.value, trend: data?.businessAnchors?.grossProfit?.trend },
    { label: 'Profit Margin', value: data?.businessAnchors?.profitMargin?.value, trend: data?.businessAnchors?.profitMargin?.trend }
  ];

  anchors?.forEach((anchor) => {
    if (yPosition > maxY) {
      pdf?.addPage();
      yPosition = margin;
    }
    pdf?.setFont(undefined, 'bold');
    pdf?.text(`${anchor?.label}:`, margin, yPosition);
    pdf?.setFont(undefined, 'normal');
    pdf?.text(`${anchor?.value} (${anchor?.trend})`, margin + 50, yPosition);
    yPosition += 6;
  });

  yPosition += 5;

  // Enterprise Health Section
  if (yPosition > maxY - 20) {
    pdf?.addPage();
    yPosition = margin;
  }

  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text('Enterprise Health', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(0, 0, 0);
  pdf?.setFont(undefined, 'bold');
  pdf?.text('Overall Performance:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  yPosition += 5;
  pdf?.text(`Status: ${data?.enterpriseHealth?.overallPerformance?.status}`, margin + 5, yPosition);
  yPosition += 5;
  const perfDesc = pdf?.splitTextToSize(data?.enterpriseHealth?.overallPerformance?.description, pageWidth - 2 * margin - 5);
  pdf?.text(perfDesc, margin + 5, yPosition);
  yPosition += perfDesc?.length * 5 + 5;

  pdf?.setFont(undefined, 'bold');
  pdf?.text('Business Health:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  yPosition += 5;
  pdf?.text(`Status: ${data?.enterpriseHealth?.businessHealth?.status}`, margin + 5, yPosition);
  yPosition += 5;
  const healthDesc = pdf?.splitTextToSize(data?.enterpriseHealth?.businessHealth?.description, pageWidth - 2 * margin - 5);
  pdf?.text(healthDesc, margin + 5, yPosition);
  yPosition += healthDesc?.length * 5 + 5;

  // Efficiency Metrics
  if (yPosition > maxY - 20) {
    pdf?.addPage();
    yPosition = margin;
  }

  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text('Efficiency & Value', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(0, 0, 0);
  pdf?.setFont(undefined, 'bold');
  pdf?.text('Cost vs Performance Ratio:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  pdf?.text(`${data?.efficiency?.costVsPerformance?.value} (${data?.efficiency?.costVsPerformance?.trend})`, margin + 60, yPosition);
  yPosition += 6;

  pdf?.setFont(undefined, 'bold');
  pdf?.text('Value Leakage:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  pdf?.text(`${data?.efficiency?.valueLeakage?.value} (${data?.efficiency?.valueLeakage?.trend})`, margin + 60, yPosition);
  yPosition += 10;

  // Risk & Stability
  if (yPosition > maxY - 20) {
    pdf?.addPage();
    yPosition = margin;
  }

  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text('Risk & Stability', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(0, 0, 0);
  pdf?.setFont(undefined, 'bold');
  pdf?.text('Execution Risk:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  yPosition += 5;
  pdf?.text(`Status: ${data?.risk?.executionRisk?.status}`, margin + 5, yPosition);
  yPosition += 5;
  const riskDesc = pdf?.splitTextToSize(data?.risk?.executionRisk?.description, pageWidth - 2 * margin - 5);
  pdf?.text(riskDesc, margin + 5, yPosition);
  yPosition += riskDesc?.length * 5 + 5;

  pdf?.setFont(undefined, 'bold');
  pdf?.text('Business Stability:', margin, yPosition);
  pdf?.setFont(undefined, 'normal');
  yPosition += 5;
  pdf?.text(`Status: ${data?.risk?.businessStability?.status}`, margin + 5, yPosition);
  yPosition += 5;
  const stabilityDesc = pdf?.splitTextToSize(data?.risk?.businessStability?.description, pageWidth - 2 * margin - 5);
  pdf?.text(stabilityDesc, margin + 5, yPosition);
  yPosition += stabilityDesc?.length * 5 + 10;

  // AI Insights Summary
  if (yPosition > maxY - 30) {
    pdf?.addPage();
    yPosition = margin;
  }

  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text('AI-Generated Insights Summary', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(0, 0, 0);
  
  const insights = [
    { label: 'Execution Health Score', value: `${data?.aiInsights?.performance?.executionHealthScore?.score}/${data?.aiInsights?.performance?.executionHealthScore?.maxScore}` },
    { label: 'Cost Leakage Indicator', value: `${data?.aiInsights?.financial?.costLeakage?.score}/${data?.aiInsights?.financial?.costLeakage?.maxScore}` },
    { label: 'Cost Stability Score', value: `${data?.aiInsights?.financial?.costStability?.score}/${data?.aiInsights?.financial?.costStability?.maxScore}` },
    { label: 'Attrition Risk', value: `${data?.aiInsights?.workforce?.attritionRisk?.score}/${data?.aiInsights?.workforce?.attritionRisk?.maxScore}` }
  ];

  insights?.forEach((insight) => {
    if (yPosition > maxY) {
      pdf?.addPage();
      yPosition = margin;
    }
    pdf?.setFont(undefined, 'bold');
    pdf?.text(`${insight?.label}:`, margin, yPosition);
    pdf?.setFont(undefined, 'normal');
    pdf?.text(insight?.value, margin + 70, yPosition);
    yPosition += 6;
  });

  return yPosition;
}

/**
 * Add drill-down analysis content to PDF
 */
async function addDrillDownContent(pdf, data, yPosition, margin, pageWidth, pageHeight) {
  const maxY = pageHeight - 20;

  pdf?.setFontSize(14);
  pdf?.setTextColor(30, 58, 138);
  pdf?.text(data?.sectionTitle || 'Drill-Down Analysis', margin, yPosition);
  yPosition += 8;

  pdf?.setFontSize(10);
  pdf?.setTextColor(100, 100, 100);
  const description = pdf?.splitTextToSize(data?.description || '', pageWidth - 2 * margin);
  pdf?.text(description, margin, yPosition);
  yPosition += description?.length * 5 + 10;

  // Add tables if present
  if (data?.tables && data?.tables?.length > 0) {
    data?.tables?.forEach((table) => {
      if (yPosition > maxY - 40) {
        pdf?.addPage();
        yPosition = margin;
      }

      pdf?.setFontSize(12);
      pdf?.setTextColor(30, 58, 138);
      pdf?.text(table?.title, margin, yPosition);
      yPosition += 8;

      // Table headers
      pdf?.setFontSize(9);
      pdf?.setFont(undefined, 'bold');
      pdf?.setTextColor(0, 0, 0);
      
      const colWidth = (pageWidth - 2 * margin) / table?.headers?.length;
      table?.headers?.forEach((header, index) => {
        pdf?.text(header, margin + index * colWidth, yPosition);
      });
      yPosition += 6;

      // Table rows
      pdf?.setFont(undefined, 'normal');
      table?.rows?.forEach((row) => {
        if (yPosition > maxY) {
          pdf?.addPage();
          yPosition = margin;
        }
        row?.forEach((cell, index) => {
          pdf?.text(String(cell), margin + index * colWidth, yPosition);
        });
        yPosition += 5;
      });

      yPosition += 10;
    });
  }

  return yPosition;
}

/**
 * Export data to CSV format
 * @param {Object} options - Export options
 * @param {string} options.title - Report title
 * @param {Array} options.data - Array of data objects to export
 * @param {Array} options.columns - Column definitions (optional)
 */
export function exportToCSV(options) {
  const { title, data, columns } = options;
  
  try {
    trackFeatureUsage('csv_export', { title, rowCount: data?.length });

    // Convert data to CSV
    const csv = Papa?.unparse(data, {
      columns: columns || undefined,
      header: true
    });

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', `${title?.replace(/\s+/g, '_')}_${new Date()?.getTime()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    
    return { success: true };
  } catch (error) {
    console.error('CSV export error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Export executive dashboard to CSV
 * @param {Object} data - Dashboard data from dataContextService
 */
export function exportExecutiveDashboardToCSV(data) {
  const csvData = [
    // Business Anchors
    { Category: 'Business Anchors', Metric: 'Total Revenue', Value: data?.businessAnchors?.totalRevenue?.value, Trend: data?.businessAnchors?.totalRevenue?.trend },
    { Category: 'Business Anchors', Metric: 'Total Cost', Value: data?.businessAnchors?.totalCost?.value, Trend: data?.businessAnchors?.totalCost?.trend },
    { Category: 'Business Anchors', Metric: 'Gross Profit', Value: data?.businessAnchors?.grossProfit?.value, Trend: data?.businessAnchors?.grossProfit?.trend },
    { Category: 'Business Anchors', Metric: 'Profit Margin', Value: data?.businessAnchors?.profitMargin?.value, Trend: data?.businessAnchors?.profitMargin?.trend },
    
    // Enterprise Health
    { Category: 'Enterprise Health', Metric: 'Overall Performance', Value: data?.enterpriseHealth?.overallPerformance?.status, Trend: data?.enterpriseHealth?.overallPerformance?.description },
    { Category: 'Enterprise Health', Metric: 'Business Health', Value: data?.enterpriseHealth?.businessHealth?.status, Trend: data?.enterpriseHealth?.businessHealth?.description },
    
    // Efficiency
    { Category: 'Efficiency & Value', Metric: 'Cost vs Performance Ratio', Value: data?.efficiency?.costVsPerformance?.value, Trend: data?.efficiency?.costVsPerformance?.trend },
    { Category: 'Efficiency & Value', Metric: 'Value Leakage', Value: data?.efficiency?.valueLeakage?.value, Trend: data?.efficiency?.valueLeakage?.trend },
    
    // Risk
    { Category: 'Risk & Stability', Metric: 'Execution Risk', Value: data?.risk?.executionRisk?.status, Trend: data?.risk?.executionRisk?.description },
    { Category: 'Risk & Stability', Metric: 'Business Stability', Value: data?.risk?.businessStability?.status, Trend: data?.risk?.businessStability?.description },
  ];

  return exportToCSV({
    title: 'Executive_Dashboard_KPIs',
    data: csvData,
    columns: ['Category', 'Metric', 'Value', 'Trend']
  });
}

/**
 * Export drill-down analysis to CSV
 * @param {string} analysisType - Type of analysis (revenue, execution, resources, costs, profit)
 * @param {Array} data - Analysis data
 */
export function exportDrillDownToCSV(analysisType, data) {
  return exportToCSV({
    title: `${analysisType}_Analysis`,
    data: data
  });
}

/**
 * Export audit logs to CSV
 * @param {Array} auditLogs - Array of audit log objects
 */
export function exportAuditLogsToCSV(auditLogs) {
  const csvData = auditLogs?.map(log => ({
    Timestamp: new Date(log?.timestamp)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
    User: log?.userName || 'Unknown',
    Email: log?.userEmail || 'N/A',
    Action: log?.actionType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' '),
    Entity: log?.entityType?.charAt(0)?.toUpperCase() + log?.entityType?.slice(1),
    EntityName: log?.entityName || 'N/A',
    Description: log?.description,
    IPAddress: log?.ipAddress || 'N/A',
  }));

  return exportToCSV({
    title: 'Audit_Logs',
    data: csvData,
    columns: ['Timestamp', 'User', 'Email', 'Action', 'Entity', 'EntityName', 'Description', 'IPAddress']
  });
}

/**
 * Export audit logs to PDF
 * @param {Array} auditLogs - Array of audit log objects
 */
export async function exportAuditLogsToPDF(auditLogs) {
  try {
    trackFeatureUsage('pdf_export', { type: 'audit-logs', count: auditLogs?.length });
    
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf?.internal?.pageSize?.getWidth();
    const pageHeight = pdf?.internal?.pageSize?.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Add header
    pdf?.setFontSize(20);
    pdf?.setTextColor(30, 58, 138);
    pdf?.text('Audit Log Report', margin, yPosition);
    yPosition += 10;

    pdf?.setFontSize(10);
    pdf?.setTextColor(100, 100, 100);
    pdf?.text(`Generated on: ${new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPosition);
    pdf?.text(`Total Records: ${auditLogs?.length}`, pageWidth - margin - 40, yPosition);
    yPosition += 15;

    // Add audit logs
    const maxY = pageHeight - 20;
    
    auditLogs?.forEach((log, index) => {
      // Check if we need a new page
      if (yPosition > maxY - 40) {
        pdf?.addPage();
        yPosition = margin;
      }

      // Log entry header
      pdf?.setFontSize(11);
      pdf?.setTextColor(30, 58, 138);
      pdf?.text(`${index + 1}. ${log?.actionType?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')} - ${log?.entityType?.charAt(0)?.toUpperCase() + log?.entityType?.slice(1)}`, margin, yPosition);
      yPosition += 6;

      // Description
      pdf?.setFontSize(9);
      pdf?.setTextColor(0, 0, 0);
      const descLines = pdf?.splitTextToSize(log?.description, pageWidth - 2 * margin);
      pdf?.text(descLines, margin, yPosition);
      yPosition += descLines?.length * 4 + 2;

      // Details
      pdf?.setFontSize(8);
      pdf?.setTextColor(100, 100, 100);
      pdf?.text(`User: ${log?.userName || 'Unknown'} (${log?.userEmail || 'N/A'})`, margin, yPosition);
      yPosition += 4;
      
      if (log?.entityName) {
        pdf?.text(`Entity: ${log?.entityName}`, margin, yPosition);
        yPosition += 4;
      }
      
      pdf?.text(`Timestamp: ${new Date(log?.timestamp)?.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })}`, margin, yPosition);
      yPosition += 4;
      
      if (log?.ipAddress) {
        pdf?.text(`IP Address: ${log?.ipAddress}`, margin, yPosition);
        yPosition += 4;
      }

      // Separator line
      pdf?.setDrawColor(200, 200, 200);
      pdf?.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
      yPosition += 8;
    });

    // Add footer
    const totalPages = pdf?.internal?.pages?.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf?.setPage(i);
      pdf?.setFontSize(8);
      pdf?.setTextColor(150, 150, 150);
      pdf?.text(
        `Page ${i} of ${totalPages} | ORIN Decision Intelligence System`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const fileName = `Audit_Logs_${new Date()?.getTime()}.pdf`;
    pdf?.save(fileName);
    
    return { success: true, fileName };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, error: error?.message };
  }
}

export default {
  exportToPDF,
  exportToCSV,
  exportExecutiveDashboardToCSV,
  exportDrillDownToCSV,
  exportAuditLogsToCSV,
  exportAuditLogsToPDF
};